import { Link } from "react-router-dom";
import { type ReactNode } from "react";
import {
  Activity,
  ArrowRight,
  FileCheck2,
  Lock,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { HomeProductPeek } from "../components/HomeProductPeek";
import { Container, Heading, LinkButton } from "../components/ui";
import { Seo } from "../lib/head";
import { SITE_URL } from "../lib/env";

const SCHEMA_ORG = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      name: "WeConsult Rx",
      url: SITE_URL,
      logo: `${SITE_URL}/logo-icon.svg`,
      sameAs: [],
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "sales",
          email: "hello@weconsultrx.com",
          areaServed: "US",
        },
      ],
      description:
        "Modern consultant pharmacy operations platform — clinical engine, compliance automation, and AI-augmented workflow for SNF, ASC, and dialysis.",
    },
    {
      "@type": "SoftwareApplication",
      name: "WeConsult Rx",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: SITE_URL,
      offers: { "@type": "Offer", category: "SaaS", url: `${SITE_URL}/pricing` },
      audience: { "@type": "Audience", audienceType: "Consultant pharmacists" },
      description:
        "Consultant pharmacist software that runs the monthly medication regimen review end to end — chart ingest, ASCP/Beers/STOPP rules and AI flags, prescriber and DON e-sign, and QuickBooks invoicing for SNF, ASC, and dialysis.",
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is WeConsult Rx?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Consultant pharmacist software that runs the monthly chart review from EHR ingest through rules-and-AI flags, prescriber and DON e-signature, and QuickBooks Online invoicing — for SNF medication regimen reviews plus ASC and dialysis compliance.",
          },
        },
        {
          "@type": "Question",
          name: "Does it integrate with PointClickCare?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Charts are ingested from PointClickCare and other EHRs via FHIR, so each month's data loads without manual re-keying.",
          },
        },
        {
          "@type": "Question",
          name: "Which CMS F-tags does it help document?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The clinical engine flags medication-related survey risks aligned to CMS F-tag guidance, including F756 drug regimen review, F757 unnecessary drugs, F758 psychotropics and gradual dose reduction, F605 chemical restraint, and F881 antibiotic stewardship.",
          },
        },
        {
          "@type": "Question",
          name: "Is it HIPAA-compliant?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "WeConsult Rx is built for a HIPAA-conscious clinical workflow with a BAA available; product demos use synthetic data only, never real resident PHI.",
          },
        },
      ],
    },
  ],
};

const STATS = [
  { value: "3", label: "Vertical rule packs" },
  { value: "7", label: "Assessment worksheets" },
  { value: "HIPAA", label: "Aligned controls" },
  { value: "7 yr", label: "Audit retention" },
] as const;

const COMPLIANCE = [
  "ASCP",
  "AAAHC pharma",
  "CMS CfC §494",
  "AAMI Water",
  "Beers 2023",
  "STOPP/START v2",
] as const;

const PILLARS = [
  {
    icon: <Activity className="h-4 w-4" aria-hidden />,
    title: "Clinical engine",
    body: "Evidence-cited rules across ACB, STOPP/START, Beers 2023, CDC core elements, and AAMI water. Every finding tied to a source.",
  },
  {
    icon: <ShieldCheck className="h-4 w-4" aria-hidden />,
    title: "Compliance automation",
    body: "CMS F-tags, state board filings, Adobe Sign workflows, and append-only audit log.",
  },
  {
    icon: <Sparkles className="h-4 w-4" aria-hidden />,
    title: "AI-augmented workflow",
    body: "AI-assisted recommendation drafting with per-tenant learning. Clinical facts are extracted deterministically — never by the model.",
  },
] as const;

const OUTCOMES = [
  {
    num: "01",
    title: "Deterministic flags",
    body: "Every clinical finding tied to a published rule or regulatory reference — auditable, not black-box.",
  },
  {
    num: "02",
    title: "Review to bundle",
    body: "Draft through signed in one pipeline: flags, recommendations, Adobe Sign, QuickBooks invoicing.",
  },
  {
    num: "03",
    title: "HIPAA by design",
    body: "Tenant-scoped data, append-only audit log, and automated PHI safety checks on every release.",
  },
] as const;

const WORKFLOW = [
  { step: "Step 1", title: "Ingest", body: "EHR data normalized and ready for rules." },
  { step: "Step 2", title: "Flag", body: "Clinical engine runs with cited evidence." },
  { step: "Step 3", title: "Review", body: "Pharmacist decisions on every finding." },
  { step: "Step 4", title: "Sign & invoice", body: "Bundle, sign-off, and billing in one flow." },
] as const;

const VERTICALS = [
  {
    tag: "Long-term care",
    title: "SNF",
    body: "ASCP standards · F605 / F757 / F881 · Boustani ACB · GDR psychotropic",
    to: "/solutions/snf",
  },
  {
    tag: "Ambulatory surgical",
    title: "ASC",
    body: "AAAHC pharmaceutical standards · DEA · high-alert med monitoring",
    to: "/solutions/asc",
  },
  {
    tag: "Dialysis",
    title: "Dialysis",
    body: "42 CFR 494 · AAMI water · ESA Hgb · MBD labs",
    to: "/solutions/dialysis",
  },
] as const;

const TRUST_ITEMS = [
  { icon: <Lock className="h-3.5 w-3.5" aria-hidden />, label: "AES-256 at rest · TLS 1.2+ in transit" },
  { icon: <ShieldCheck className="h-3.5 w-3.5" aria-hidden />, label: "HIPAA §164.308 / .310 / .312 controls" },
  { icon: <FileCheck2 className="h-3.5 w-3.5" aria-hidden />, label: "BAA on every covered subprocessor" },
  { icon: <Activity className="h-3.5 w-3.5" aria-hidden />, label: "Append-only audit log · 7-year retention" },
] as const;

function SectionEyebrow({ children }: { children: ReactNode }) {
  return <p className="text-sm font-medium text-forest-700">{children}</p>;
}

function SectionLede({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <p className={`max-w-[480px] text-[0.9375rem] leading-relaxed text-ink-500 ${className}`}>
      {children}
    </p>
  );
}

export function Home() {
  return (
    <>
      <Seo title="Consultant Pharmacist Software | WeConsult Rx" description="Consultant pharmacist software that runs the monthly medication regimen review end to end: chart ingest, ASCP/Beers/STOPP rules and AI flags, prescriber and DON e-sign, and QuickBooks invoicing for SNF, ASC, and dialysis." path="/" jsonLd={SCHEMA_ORG} />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-surface pb-0 pt-16 md:pt-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-55"
          style={{
            backgroundImage:
              "linear-gradient(#f1f5f9 1px, transparent 1px), linear-gradient(90deg, #f1f5f9 1px, transparent 1px)",
            backgroundSize: "80px 80px",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 15%, transparent 65%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 60% at 50% 30%, black 15%, transparent 65%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-ink-200 to-transparent"
        />

        <Container className="relative z-[1] text-center">
          <div className="mx-auto max-w-[640px] pb-10">
            <span className="inline-flex items-center rounded-md border border-forest-100 bg-forest-50 px-2.5 py-1 text-xs font-medium text-forest-700">
              HIPAA-grade · consultant pharmacy operations
            </span>
            <Heading
              level={1}
              className="mt-6 text-[clamp(2.25rem,5vw,3.25rem)] font-semibold leading-[1.1] tracking-tightest"
            >
              Modern chart review,
              <br />
              structured and auditable.
            </Heading>
            <p className="mx-auto mt-5 max-w-[520px] text-[1.0625rem] leading-relaxed text-ink-500">
              Clinical rules, compliance automation, and sign-off in one workflow — built for SNF,
              ASC, and dialysis consultant pharmacists.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5">
              <LinkButton to="/demo" size="lg">
                Request a demo
              </LinkButton>
              <LinkButton to="/platform" variant="secondary" size="lg">
                Explore platform
              </LinkButton>
            </div>
            <p className="mt-3 text-sm text-ink-400">
              See the full workflow in a guided demo — not on a landing page.
            </p>
          </div>
        </Container>

        <HomeProductPeek />
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-ink-50">
        <div className="mx-auto grid max-w-container grid-cols-2 gap-px bg-border md:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="bg-ink-50 px-6 py-7 text-center md:px-8">
              <div className="text-[1.75rem] font-semibold tracking-tightest text-ink-900">
                {s.value}
              </div>
              <div className="mt-0.5 text-xs text-ink-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Compliance strip */}
      <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 border-b border-border px-6 py-6">
        {COMPLIANCE.map((item) => (
          <span
            key={item}
            className="text-[0.7rem] font-semibold uppercase tracking-widest text-ink-400"
          >
            {item}
          </span>
        ))}
      </div>

      {/* Three pillars */}
      <section className="border-b border-border py-20">
        <Container>
          <SectionEyebrow>The platform</SectionEyebrow>
          <Heading level={2} className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
            Three pillars. One workflow.
          </Heading>
          <SectionLede className="mt-3">
            Clinical depth and operational throughput — without spreadsheets or manual bundling.
          </SectionLede>

          <div className="mt-12 overflow-hidden rounded-[14px] border border-border bg-border md:grid md:grid-cols-3 md:gap-px">
            {PILLARS.map((p) => (
              <article
                key={p.title}
                className="bg-surface p-7 transition-colors hover:bg-ink-50"
              >
                <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-ink-50 text-forest-700">
                  {p.icon}
                </div>
                <h3 className="text-[0.9375rem] font-semibold text-ink-900">{p.title}</h3>
                <p className="mt-2 text-[0.8125rem] leading-relaxed text-ink-500">{p.body}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* Outcomes + workflow */}
      <section className="border-b border-border bg-ink-50 py-20">
        <Container>
          <SectionEyebrow>What you get</SectionEyebrow>
          <Heading level={2} className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
            Outcomes, not screenshots.
          </Heading>
          <SectionLede className="mt-3">
            We show capabilities and results on the site. The full review workspace is reserved for
            customers and demos.
          </SectionLede>

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {OUTCOMES.map((o) => (
              <article
                key={o.num}
                className="rounded-[14px] border border-border bg-surface p-6"
              >
                <div className="mb-3 text-[0.7rem] font-semibold uppercase tracking-wider text-ink-400">
                  {o.num}
                </div>
                <h3 className="text-[0.9375rem] font-semibold text-ink-900">{o.title}</h3>
                <p className="mt-2 text-[0.8125rem] leading-relaxed text-ink-500">{o.body}</p>
              </article>
            ))}
          </div>

          <div className="mt-12 overflow-hidden rounded-[14px] border border-border md:grid md:grid-cols-4">
            {WORKFLOW.map((w, i) => (
              <div
                key={w.title}
                className={`bg-surface p-6 ${
                  i < WORKFLOW.length - 1 ? "border-b border-border md:border-b-0 md:border-r" : ""
                }`}
              >
                <div className="text-[0.7rem] font-semibold text-forest-700">{w.step}</div>
                <h4 className="mt-2 text-sm font-semibold text-ink-900">{w.title}</h4>
                <p className="mt-1.5 text-[0.8125rem] leading-snug text-ink-500">{w.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid items-center gap-8 rounded-[14px] border border-border bg-surface p-8 md:grid-cols-[1fr_auto] md:p-10">
            <div>
              <h3 className="text-[1.0625rem] font-semibold text-ink-900">
                Want to see the full platform?
              </h3>
              <p className="mt-1.5 max-w-[420px] text-sm text-ink-500">
                We walk through a live review with your sample chart in 30 minutes — that&apos;s
                where the product speaks for itself.
              </p>
            </div>
            <LinkButton to="/demo" size="lg" className="shrink-0">
              Book a demo
              <ArrowRight className="h-4 w-4" aria-hidden />
            </LinkButton>
          </div>
        </Container>
      </section>

      {/* Solutions */}
      <section className="border-b border-border py-20">
        <Container>
          <SectionEyebrow>Solutions</SectionEyebrow>
          <Heading level={2} className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
            Built for your facility type.
          </Heading>
          <SectionLede className="mt-3">
            Rule packs and compliance surfaces tuned to each regulatory framework.
          </SectionLede>

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {VERTICALS.map((v) => (
              <article
                key={v.title}
                className="rounded-[14px] border border-border bg-surface p-6 transition-shadow hover:border-ink-300 hover:shadow-cardHover"
              >
                <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-400">
                  {v.tag}
                </div>
                <h3 className="mt-2 text-[1.0625rem] font-semibold text-ink-900">{v.title}</h3>
                <p className="mt-2 text-[0.8125rem] leading-relaxed text-ink-500">{v.body}</p>
                <Link
                  to={v.to}
                  className="mt-4 inline-flex items-center gap-1 text-[0.8125rem] font-medium text-forest-700 hover:text-forest-900"
                >
                  Explore {v.title}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* Trust */}
      <section className="py-20">
        <Container>
          <div className="grid gap-12 rounded-[14px] border border-border bg-ink-50 p-8 md:grid-cols-[1.1fr_1fr] md:p-10">
            <div>
              <SectionEyebrow>Trust &amp; compliance</SectionEyebrow>
              <Heading level={2} className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
                Compliance shipped in code.
              </Heading>
              <SectionLede className="mt-3">
                Automated PHI safety checks and tenant isolation testing on every release.
              </SectionLede>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <LinkButton to="/trust" size="md">
                  Trust center
                </LinkButton>
                <LinkButton to="/hipaa" variant="secondary" size="md">
                  HIPAA controls
                </LinkButton>
              </div>
            </div>
            <ul className="grid gap-2">
              {TRUST_ITEMS.map((item) => (
                <li
                  key={item.label}
                  className="flex items-center gap-3 rounded-[10px] border border-border bg-surface px-4 py-3 text-[0.8125rem] font-medium text-ink-700"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-ink-50 text-forest-700">
                    {item.icon}
                  </span>
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-ink-50 py-20 text-center">
        <Container>
          <Heading level={2} className="text-3xl font-semibold tracking-tight md:text-4xl">
            See WeConsult Rx in your workflow.
          </Heading>
          <p className="mx-auto mt-4 max-w-[440px] text-[0.9375rem] text-ink-500">
            Bring a sample chart. We&apos;ll show you a flagged review and bundle inside 30 minutes
            — with citations.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            <LinkButton to="/demo" size="lg">
              Request a demo
            </LinkButton>
            <LinkButton to="/contact" variant="secondary" size="lg">
              Talk to us
            </LinkButton>
          </div>
        </Container>
      </section>
    </>
  );
}
